// Menú lateral (bloque 7, A; K2 aprobado por Kevin el 19/10: menú = flujo,
// Checklist = orden legal). Aquí están los datos sin íconos para poder probar el
// árbol y la visibilidad; Dashboard.tsx agrega el ícono de cada entrada.
// Solo cambian el orden y la sección: rutas, permisos y visibilidad
// (hiddenForClients, minRole, superadminOnly) son los mismos que antes.

import type { ChecklistItem } from './compliance-engine';

export type NavItem = 'dashboard' | 'sedes' | 'productos' | 'compatibilidad' | 'bodegas' | 'usuarios' | 'reportes' | 'emergencias' | 'auditoria' | 'layout' | 'configuracion' | 'terminos' | 'superadmin' | 'sso-perfil' | 'trabajadores' | 'puestos-trabajo' | 'incidentes' | 'dashboard-sso' | 'epp' | 'calendario-sso' | 'documentos-sso' | 'capacitaciones' | 'etiquetado-sga' | 'inspecciones' | 'inspecciones-v2' | 'iper' | 'vigilancia-salud' | 'permisos-trabajo' | 'checklist' | 'plan-emergencias' | 'plan-emergencias-v2' | 'riesgo-psicosocial' | 'constructor-gestion-tecnica' | 'metodologia-coshh' | 'higiene-fisico' | 'higiene-biologico' | 'metodologia-biogaval' | 'metodologia-iluminacion' | 'metodologia-ruido' | 'metodologia-estres-termico' | 'metodologia-rula' | 'metodologia-reba' | 'ergonomia-manipulacion-cargas' | 'metodologia-snook-ciriello' | 'ergonomia-repetitividad' | 'ergonomia-puestos-oficina' | 'metodologia-rosa' | 'learning' | 'cuestionario-psicosocial' | 'resultados-psicosocial' | 'inventario-activos'
  // Página común de un ítem del Anexo 1 (bloque 7, B y C): 'item:GA-03', 'item:SP-01'…
  | `item:${string}`;

export type RolMinimo = 'viewer' | 'admin' | 'manager';

export interface EntradaMenu {
  id:                NavItem;
  label:             string;
  minRole?:          RolMinimo;
  superadminOnly?:   boolean;
  hiddenForClients?: boolean;
  separator?:        boolean;
  badge?:            string;
  section?:          string;
  subsection?:       string;
  group?:            string;
  /** Nota corta junto a la etiqueta (p. ej. "Sin calcular"). */
  nota?:             string;
}

export const SECCION = {
  general:     'General',
  empresa:     'Empresa',
  ga:          'Gestión Administrativa',
  gt:          'Gestión Técnica',
  th:          'Gestión del Talento Humano',
  po:          'Procedimientos Operativos Básicos',
  sp:          'Servicios Permanentes',
  mant:        'Mantenimiento',
  config:      'Configuraciones',
} as const;

export const GRUPO_ITEMS_ANEXO = 'Ítems del Anexo 1';

const GT = SECCION.gt;
const QUIMICO = 'Higiene y Gestión del Riesgo Químico';
const FISICO = 'Higiene y Gestión del Riesgo Físico';
const BIOLOGICO = 'Higiene y Gestión del Riesgo Biológico';

export const MENU_BASE: EntradaMenu[] = [
  // ── General (Calendario deja de ser una sección propia) ──
  { id: 'dashboard',       label: 'Dashboard',      section: SECCION.general },
  { id: 'dashboard-sso',   label: 'Dashboard SSO',  minRole: 'manager', section: SECCION.general, hiddenForClients: true },
  { id: 'calendario-sso',  label: 'Calendario SSO', minRole: 'manager', section: SECCION.general, hiddenForClients: true },

  // ── Empresa. Sedes (centros de trabajo) entra aquí en F1a, oculta hasta que Kevin la valide. ──
  { id: 'sedes',           label: 'Sedes',              minRole: 'manager', section: SECCION.empresa, hiddenForClients: true },
  { id: 'puestos-trabajo', label: 'Puestos de Trabajo', minRole: 'manager', section: SECCION.empresa, hiddenForClients: true },
  { id: 'trabajadores',    label: 'Trabajadores',       minRole: 'manager', section: SECCION.empresa, hiddenForClients: true },

  // ── Gestión Administrativa (los ítems del Anexo 1 se insertan después de Perfil SSO) ──
  { id: 'documentos-sso', label: 'Documentos SSO',    section: SECCION.ga, hiddenForClients: true },
  { id: 'checklist',      label: 'Checklist Anexo 1', minRole: 'manager', section: SECCION.ga, hiddenForClients: true },
  { id: 'sso-perfil',     label: 'Perfil SSO',        minRole: 'manager', section: SECCION.ga, hiddenForClients: true },

  // ── Gestión Técnica (Psicosocial y Química se quedan aquí) ──
  { id: 'iper',                        label: 'Matriz de Riesgos',       section: GT, group: 'Identificación de Riesgo', hiddenForClients: true },
  { id: 'constructor-gestion-tecnica', label: 'Constructor Inteligente', minRole: 'manager', section: GT, group: 'Identificación de Riesgo', hiddenForClients: true },
  { id: 'permisos-trabajo',            label: 'Permisos de Trabajo',     minRole: 'viewer', section: GT, group: 'Identificación de Riesgo', hiddenForClients: true },
  { id: 'productos',      label: 'Productos',                  section: GT, group: 'Higiene Industrial', subsection: QUIMICO },
  { id: 'compatibilidad', label: 'Matriz de Compatibilidad',   section: GT, group: 'Higiene Industrial', subsection: QUIMICO },
  { id: 'bodegas',        label: 'Bodegas',                    section: GT, group: 'Higiene Industrial', subsection: QUIMICO },
  { id: 'emergencias',    label: 'Plan de Emergencia Química', minRole: 'manager', section: GT, group: 'Higiene Industrial', subsection: QUIMICO },
  { id: 'etiquetado-sga', label: 'Etiquetado SGA',             section: GT, group: 'Higiene Industrial', subsection: QUIMICO },
  { id: 'layout',         label: 'Layout Pro',                 minRole: 'manager', section: GT, group: 'Higiene Industrial', subsection: QUIMICO, badge: 'PREMIUM' },
  { id: 'metodologia-coshh', label: 'Metodología COSHH',       minRole: 'manager', section: GT, group: 'Higiene Industrial', subsection: QUIMICO },
  { id: 'higiene-fisico',             label: 'Higiene y Gestión del Riesgo Físico', minRole: 'manager', section: GT, group: 'Higiene Industrial', subsection: FISICO, hiddenForClients: true },
  { id: 'metodologia-iluminacion',    label: 'Metodología de Iluminación',          minRole: 'manager', section: GT, group: 'Higiene Industrial', subsection: FISICO, hiddenForClients: true },
  { id: 'metodologia-ruido',          label: 'Metodología de Ruido',                minRole: 'manager', section: GT, group: 'Higiene Industrial', subsection: FISICO, hiddenForClients: true },
  { id: 'metodologia-estres-termico', label: 'Metodología de Estrés Térmico',       minRole: 'manager', section: GT, group: 'Higiene Industrial', subsection: FISICO, hiddenForClients: true },
  { id: 'higiene-biologico',    label: 'Higiene y Gestión del Riesgo Biológico', minRole: 'manager', section: GT, group: 'Higiene Industrial', subsection: BIOLOGICO, hiddenForClients: true },
  { id: 'metodologia-biogaval', label: 'Metodología BIOGAVAL',                   minRole: 'manager', section: GT, group: 'Higiene Industrial', subsection: BIOLOGICO, hiddenForClients: true },
  { id: 'metodologia-rula',              label: 'Metodología RULA',  minRole: 'manager', section: GT, group: 'Ergonomía', subsection: 'Carga Postural', hiddenForClients: true },
  { id: 'metodologia-reba',              label: 'Metodología REBA',  minRole: 'manager', section: GT, group: 'Ergonomía', subsection: 'Carga Postural', hiddenForClients: true },
  { id: 'ergonomia-manipulacion-cargas', label: 'Ecuación de NIOSH', minRole: 'manager', section: GT, group: 'Ergonomía', subsection: 'Manipulación de Cargas', hiddenForClients: true },
  { id: 'metodologia-snook-ciriello',    label: 'Snook y Ciriello',  minRole: 'manager', section: GT, group: 'Ergonomía', subsection: 'Manipulación de Cargas', hiddenForClients: true },
  { id: 'ergonomia-repetitividad',       label: 'Repetitividad',     minRole: 'manager', section: GT, group: 'Ergonomía', subsection: 'Repetitividad', hiddenForClients: true },
  { id: 'ergonomia-puestos-oficina',     label: 'Método ROSA',       minRole: 'manager', section: GT, group: 'Ergonomía', subsection: 'Puestos Oficina', hiddenForClients: true },
  { id: 'riesgo-psicosocial',       label: 'Riesgo Psicosocial',       section: GT, group: 'Riesgo Psicosocial', hiddenForClients: true },
  { id: 'cuestionario-psicosocial', label: 'Cuestionario Psicosocial', section: GT, group: 'Riesgo Psicosocial', hiddenForClients: true },
  { id: 'resultados-psicosocial',   label: 'Resultados Psicosocial',   minRole: 'admin', section: GT, group: 'Riesgo Psicosocial', hiddenForClients: true },

  // ── Gestión del Talento Humano (texto del Anexo 1) ──
  { id: 'capacitaciones', label: 'Capacitaciones',            section: SECCION.th, hiddenForClients: true },
  { id: 'learning',       label: 'Diseña tu propio Learning', section: SECCION.th, hiddenForClients: true },

  // ── Procedimientos Operativos Básicos (texto del Anexo 1) ──
  { id: 'inspecciones',     label: 'Inspecciones',        section: SECCION.po, hiddenForClients: true },
  // F3a: motor nuevo de inspecciones (M41), oculto para clientes como el Plan de Emergencias v2. El anterior se queda
  // (alimenta el GT-14) hasta que se decida su reemplazo (§17 ítem 164, punto 9).
  { id: 'inspecciones-v2',  label: 'Inspecciones v2',     section: SECCION.po, hiddenForClients: true },
  { id: 'vigilancia-salud', label: 'Vigilancia de Salud', minRole: 'viewer', section: SECCION.po, hiddenForClients: true },
  { id: 'plan-emergencias', label: 'Plan de Emergencias', section: SECCION.po, hiddenForClients: true },
  // PE-A3a: asistente v2 en construcción; misma visibilidad que el plan actual (EMPRESA TEST y superadmin impersonando).
  { id: 'plan-emergencias-v2', label: 'Plan de Emergencias v2', section: SECCION.po, hiddenForClients: true },
  { id: 'incidentes',       label: 'Incidentes',          minRole: 'manager', section: SECCION.po, hiddenForClients: true },
  { id: 'epp',              label: 'EPP',                 minRole: 'manager', section: SECCION.po, hiddenForClients: true },

  // ── Servicios Permanentes: sus entradas son los ítems SP que aplican (se insertan aquí) ──

  // ── Mantenimiento (M1, M50): oculto para clientes como los demás módulos nuevos. Los demás ítems llegan después. ──
  { id: 'inventario-activos', label: 'Inventario de activos', section: SECCION.mant, hiddenForClients: true },

  // ── Configuraciones ──
  { id: 'usuarios',      label: 'Usuarios',               minRole: 'manager', section: SECCION.config },
  { id: 'reportes',      label: 'Reportes',               minRole: 'manager', section: SECCION.config },
  { id: 'auditoria',     label: 'Historial de Cambios',   minRole: 'admin',   section: SECCION.config },
  { id: 'configuracion', label: 'Configuración',          minRole: 'admin',   section: SECCION.config },
  { id: 'terminos',      label: 'Términos y Condiciones', section: SECCION.config },

  // ── Super Admin (sin sección, separado visualmente) ──
  { id: 'superadmin', label: 'Super Admin', superadminOnly: true, separator: true },
];

// ── Apartados por ítem (B y C) ─────────────────────────────────────────────

export const SIN_CALCULAR = 'Sin calcular';

export function idItemAnexo(itemId: string): NavItem {
  return `item:${itemId}`;
}

export function itemDeNav(id: string): string | null {
  return id.startsWith('item:') ? id.slice(5) : null;
}

/** "GA 3" o "SP 1": número legal del Anexo 1. */
export function codigoLegal(item: Pick<ChecklistItem, 'id' | 'numero_legal'>): string {
  return item.numero_legal != null ? `${item.id.slice(0, 2)} ${item.numero_legal}` : item.id;
}

/**
 * ¿El ítem tiene su apartado en el menú? Los N/A no; con el perfil incompleto
 * (o una pregunta sin responder) sí, con la nota "Sin calcular".
 */
export function muestraApartado(item: Pick<ChecklistItem, 'aplica' | 'sin_calcular'>): boolean {
  return item.aplica || !!item.sin_calcular;
}

/** Entradas de GA (subgrupo plegable) y de Servicios Permanentes, en orden legal. */
export function entradasItemsAnexo(items: ChecklistItem[]): { ga: EntradaMenu[]; sp: EntradaMenu[] } {
  const entrada = (i: ChecklistItem, seccion: string, group?: string): EntradaMenu => ({
    id: idItemAnexo(i.id),
    label: `${codigoLegal(i)} · ${i.titulo}`,
    section: seccion,
    group,
    hiddenForClients: true,   // misma visibilidad que Documentos SSO (decisión de JC, 23/10/2026)
    nota: i.sin_calcular ? SIN_CALCULAR : undefined,
  });
  const orden = (a: ChecklistItem, b: ChecklistItem) => (a.numero_legal ?? 99) - (b.numero_legal ?? 99);
  const de = (prefijo: string) => items.filter(i => i.id.startsWith(prefijo) && muestraApartado(i)).sort(orden);
  return {
    ga: de('GA-').map(i => entrada(i, SECCION.ga, GRUPO_ITEMS_ANEXO)),
    sp: de('SP-').map(i => entrada(i, SECCION.sp)),
  };
}

/** El menú completo: los apartados GA después de Perfil SSO y los SP después de EPP. */
export function construirMenu(base: EntradaMenu[], dinamicas: { ga: EntradaMenu[]; sp: EntradaMenu[] }): EntradaMenu[] {
  const out: EntradaMenu[] = [];
  for (const e of base) {
    out.push(e);
    if (e.id === 'sso-perfil') out.push(...dinamicas.ga);
    if (e.id === 'epp') out.push(...dinamicas.sp);
  }
  return out;
}

// ── Visibilidad (igual que el filtro que tenía Dashboard.tsx) ───────────────

export interface ContextoMenu {
  impersonando: boolean;
  esSuperadmin: boolean;
  /** Empresa con is_dev = true (EMPRESA TEST). */
  veOcultos: boolean;
  /** Rol del usuario; null mientras carga el perfil. */
  rol: string | null;
}

const RANGO: Record<string, number> = { viewer: 0, manager: 1, admin: 2 };

export function entradaVisible(item: EntradaMenu, c: ContextoMenu): boolean {
  if (c.impersonando) {
    // Impersonando se ve lo que vería un cliente (como admin).
    if (item.hiddenForClients && !c.veOcultos) return false;
    return !item.superadminOnly;
  }
  if (c.esSuperadmin) return item.superadminOnly === true || item.id === 'usuarios';
  if (item.superadminOnly) return false;
  if (item.hiddenForClients && !c.veOcultos) return false;
  if (!item.minRole) return true;
  if (!c.rol) return true;
  return RANGO[c.rol] >= RANGO[item.minRole];
}

export function filtrarMenu(items: EntradaMenu[], c: ContextoMenu): EntradaMenu[] {
  return items.filter(i => entradaVisible(i, c));
}

// ── "Resolver" y flechas del Checklist (A3, B4) ────────────────────────────

/** Módulo del motor (modulo_storsafe) → entrada del menú. */
export const NAV_POR_MODULO: Record<string, NavItem> = {
  'Documentos SSO':              'documentos-sso',
  'Perfil SSO':                  'sso-perfil',
  'Vigilancia Salud':            'vigilancia-salud',
  'Permisos de Trabajo':         'permisos-trabajo',
  'Productos':                   'productos',
  'Etiquetado SGA':              'etiquetado-sga',
  'Inspecciones':                'inspecciones',
  'Puestos de Trabajo':          'puestos-trabajo',
  'Matriz de Riesgos':           'iper',
  'Trabajadores':                'trabajadores',
  'Capacitaciones':              'capacitaciones',
  'Incidentes':                  'incidentes',
  'Plan de Emergencias':         'plan-emergencias',
  'EPP':                         'epp',
  'Riesgo Psicosocial':          'riesgo-psicosocial',
  'Constructor Gestión Técnica': 'constructor-gestion-tecnica',
  // Señalización (M51): GT 31, 32, 33, 34 y 36 con la plantilla de señalización de la biblioteca.
  'Inspecciones v2 (Señalización)':          'inspecciones-v2',
  // Lote A: los ítems que calculan las inspecciones firmadas (el paréntesis dice qué plantilla usar).
  'Inspecciones v2 (Productos químicos)':    'inspecciones-v2',
  'Inspecciones v2 (Condiciones generales)': 'inspecciones-v2',
  // Mantenimiento M1: GT 10, 11, 27 y 28 se cumplen con inspecciones por activo.
  'Inventario de activos (inspecciones por activo)': 'inventario-activos',
};

/** Ítems cuya flecha abre su página común en vez del módulo: todos los GA y los SP (decisión de JC, 23/10/2026). */
export function abrePaginaDelItem(itemId: string): boolean {
  return itemId.startsWith('GA-') || itemId.startsWith('SP-');
}

export type DestinoResolver =
  | { tipo: 'item'; nav: NavItem; itemId: string }
  | { tipo: 'modulo'; nav: NavItem; modulo: string }
  | null;

export function destinoResolver(itemId: string, modulo?: string): DestinoResolver {
  if (abrePaginaDelItem(itemId)) return { tipo: 'item', nav: idItemAnexo(itemId), itemId };
  const nav = modulo ? NAV_POR_MODULO[modulo] : undefined;
  return nav ? { tipo: 'modulo', nav, modulo: modulo as string } : null;
}
